import { PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PERMISSIONS } from '@tms/shared';

import { api, errorMessage } from '@/api/client';
import { useAuthedImage } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { BatchPrintPage, useBatchIds } from '@/components/BatchPrintPage';
import { fmt } from '@/lib/labels';

interface PrintData {
  office: {
    id: number;
    organizationName: string;
    nameRu: string;
    nameUz: string;
    code: string;
    address: string | null;
    phone: string | null;
    taskLayout: string;
    taskAddressALocations: boolean;
  };
  printedAt: string;
  /** Кто допустил водителя, выпустил машину и выдал лист — для подписей. */
  conclusions: {
    medical: { name: string | null; checkedAt: string; result: string } | null;
    technical: { name: string | null; checkedAt: string; result: string } | null;
    dispatcher: { name: string; issuedAt: string | null } | null;
    /** Лист выдан в обход допуска — с причиной, которую указал диспетчер. */
    medicalBypass: { reason: string | null } | null;
    technicalBypass: { reason: string | null } | null;
  };
  waybill: {
    id: number;
    number: string;
    status: string;
    validFrom: string;
    validTo: string;
    odometerStart: string | null;
    odometerEnd: string | null;
    engineHoursStart: string | null;
    engineHoursEnd: string | null;
    distanceKm: string | null;
    engineHours: string | null;
    fuelOpening: string;
    fuelIssued: string;
    fuelConsumed: string | null;
    fuelClosing: string | null;
    fuelNorm: string | null;
    fuelDeviationPct: string | null;
    notes: string | null;
    vehicle: {
      garageNumber: string;
      plateNumber: string | null;
      meterType: string;
    } | null;
    driver: {
      lastName: string;
      firstName: string;
      middleName: string | null;
      personnelNumber: string;
    } | null;
    coDriver: {
      lastName: string;
      firstName: string;
      middleName: string | null;
      personnelNumber: string;
    } | null;
    tasks: Array<{
      id: number;
      sequence: number;
      fromPoint: string | null;
      flightNumber: string | null;
      aircraftReg: string | null;
      standNumber: string | null;
      toPoint: string | null;
      distanceKm: string | null;
      engineHours: string | null;
      notes: string | null;
    }>;
  };
}

/**
 * Печатная форма путевого листа — отдельная страница вне AppLayout.
 *
 * Открывается в новой вкладке (см. кнопку «Печать» в WaybillDrawer), поэтому
 * не тянет за собой тёмный сайдбар и шапку: печатать нужно только сам
 * документ. «Скачать PDF» отдельной кнопкой не сделано намеренно — диалог
 * печати браузера уже умеет сохранять как PDF, и это тот же самый макет,
 * без второй реализации вёрстки под другой рендерер.
 */
export function WaybillPrintPage() {
  const { id } = useParams<{ id: string }>();
  return <WaybillPrintDocument id={id} />;
}

/**
 * Печатные формы нескольких путевых листов подряд — для листов, отмеченных
 * галочками в журнале. Каждый лист начинается с новой страницы.
 */
export function WaybillBatchPrintPage() {
  const ids = useBatchIds();
  return (
    <BatchPrintPage
      ids={ids}
      render={(id) => <WaybillPrintDocument id={id} embedded />}
    />
  );
}

/**
 * Сам документ. embedded — лист внутри пачки: без собственной панели с
 * кнопкой печати (она одна на всю пачку) и без заставки на весь экран.
 */
function WaybillPrintDocument({
  id,
  embedded = false,
}: {
  id?: string;
  embedded?: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();

  // Итог подгонки листа под A4 — для предупреждения над листом.
  const [fit, setFit] = useState<FitState>({ scale: 1, overflow: false });

  const query = useQuery({
    queryKey: ['waybill-print', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<PrintData>(`/waybills/${id}/print`)).data,
  });

  if (!can(PERMISSIONS.WAYBILL_PRINT)) {
    return (
      <div style={{ padding: 48 }}>
        <Alert type="error" showIcon message={t('Нет прав на печать путевых листов')} />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          minHeight: embedded ? 120 : '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div style={{ padding: 48 }}>
        <Alert
          type="error"
          showIcon
          // В пачке листов несколько, и причины у них разные (черновик,
          // например, не печатается) — показываем ответ сервера и номер записи.
          message={
            embedded && query.error
              ? `${errorMessage(query.error)} (id ${id})`
              : t('Путевой лист не найден')
          }
        />
      </div>
    );
  }

  const { office, waybill, printedAt, conclusions } = query.data;
  const personName = (p: typeof waybill.driver): string =>
    p ? `${p.lastName} ${p.firstName} ${p.middleName ?? ''}`.trim() : '—';
  const driverName = personName(waybill.driver);
  const meterType = waybill.vehicle?.meterType;
  // Раскладка идёт с офисом, выпустившим лист (а не с активным офисом текущего
  // пользователя) — печатная форма должна отражать реальность документа.
  const isRegionDistrictLayout = office.taskLayout === 'REGION_DISTRICT';
  const isAddressLayout = office.taskLayout === 'ADDRESS';
  const hasLocationList = office.taskAddressALocations;
  // Колонок в таблице заданий: №, поля раскладки, км и мч.
  const taskColumns =
    1 + (isRegionDistrictLayout ? 3 : isAddressLayout ? (hasLocationList ? 3 : 2) : 4) + 2;

  return (
    <div className="wb-screen">
      <style>{`
        /*
          Путевой лист — строго один лист A4. На экране форма показана самим
          листом (210×297 мм), поэтому что видно, то и напечатается. Если
          содержимое длиннее листа (много заданий), FitToPage уменьшает его
          масштаб так, чтобы оно поместилось целиком, а не ушло на вторую
          страницу.
        */
        .wb-screen {
          background: #e9ebee;
          padding: 0 0 32px;
          min-height: 100%;
        }
        .wb-print {
          box-sizing: border-box;
          width: 210mm;
          height: 296.5mm;
          margin: 0 auto;
          padding: 12mm 12mm 10mm;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
          color: #1a1a1a;
          background: #fff;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
        }
        .wb-toolbar {
          position: sticky;
          top: 0;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          width: 210mm;
          max-width: 100%;
          box-sizing: border-box;
          margin: 0 auto 16px;
          padding: 12px 0;
          background: #e9ebee;
        }
        /* Фирменный бланк: логотип слева, рядом организация, офис и контакты,
           под ними двойная линия на всю ширину листа. */
        .wb-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding-bottom: 12px;
          margin-bottom: 20px;
          border-bottom: 2px solid #222;
          box-shadow: 0 3px 0 -2px #222;
        }
        .wb-header .wb-logo {
          height: 56px;
          width: auto;
          max-width: 200px;
          object-fit: contain;
          flex: none;
        }
        .wb-header .org-name {
          font-size: 11px;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #555;
          margin: 0 0 2px;
        }
        .wb-header .office-name { font-size: 16px; font-weight: 700; margin: 0; }
        .wb-header .office-contact { font-size: 12px; color: #555; margin: 2px 0 0; }
        .wb-title {
          text-align: center;
          font-size: 20px;
          font-weight: 700;
          margin: 16px 0 4px;
          letter-spacing: 0.5px;
        }
        .wb-subtitle { text-align: center; font-size: 13px; color: #555; margin-bottom: 20px; }
        table.wb-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
          font-size: 13px;
        }
        table.wb-table th, table.wb-table td {
          border: 1px solid #999;
          padding: 6px 8px;
          text-align: left;
        }
        table.wb-table th { background: #f2f2f2; font-weight: 600; }
        table.wb-table tr.wb-task-note td { font-size: 0.85em; color: #333; font-style: italic; }
        .wb-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          margin-bottom: 20px;
          border: 1px solid #999;
          font-size: 13px;
        }
        .wb-grid .cell {
          border-bottom: 1px solid #ccc;
          border-right: 1px solid #ccc;
          padding: 6px 10px;
        }
        .wb-grid .cell:nth-child(2n) { border-right: none; }
        .wb-grid .cell .label { color: #666; font-size: 11px; display: block; }
        .wb-signatures {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          margin-top: 28px;
        }
        .wb-signature-line {
          margin-top: 40px;
          border-top: 1px solid #333;
          padding-top: 4px;
          font-size: 11px;
          color: #666;
          text-align: center;
        }
        /* Строки заключений выше обычных — в последней колонке расписываются. */
        table.wb-conclusions td { height: 34px; vertical-align: middle; }
        table.wb-conclusions td.wb-bypass { font-weight: 600; }
        .wb-fit-warning {
          width: 210mm;
          max-width: 100%;
          box-sizing: border-box;
          margin: 0 auto 10px;
          padding: 10px 16px;
          border-radius: 8px;
          background: #fff7e6;
          border: 1px solid #ffd591;
          color: #ad4e00;
          font-size: 13px;
        }
        .wb-footer { margin-top: 32px; font-size: 11px; color: #999; text-align: center; }

        @page { size: A4; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          .wb-screen { background: none; padding: 0; }
          .wb-print { margin: 0; box-shadow: none; }
        }
      `}</style>

      {!embedded && (
        <div className="wb-toolbar no-print">
          <Typography.Text type="secondary">
            {t(
              'Совет: чтобы сохранить в PDF, в диалоге печати выберите «Сохранить как PDF»',
            )}
          </Typography.Text>
          <Space>
            <Button onClick={() => window.close()}>{t('Закрыть')}</Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => window.print()}
            >
              {t('Печать')}
            </Button>
          </Space>
        </div>
      )}

      {/*
        Предупреждение о сильном уменьшении — над своим листом, с номером:
        в пачке у каждого листа своё, и они не перекрывают друг друга.
        Только на экране, в печать не идёт.
      */}
      {(fit.overflow || fit.scale < READABLE_SCALE) && (
        <div className="wb-fit-warning no-print">
          {t('Путевой лист')} № {waybill.number}:{' '}
          {fit.overflow
            ? t(
                'Содержимое не помещается на один лист даже в мелком масштабе — низ листа обрезан. Сократите задания или примечание.',
              )
            : `${t('Лист уменьшен до')} ${Math.round(fit.scale * 100)}%, ${t(
                'чтобы поместиться на одну страницу — проверьте, читается ли шрифт.',
              )}`}
        </div>
      )}

      <div className="wb-print">
        <FitToPage onFit={setFit}>
          <div className="wb-header">
            <PrintLogo officeId={office.id} />
            <div>
              {/* Организацию не повторяем, если офис и есть сама организация. */}
              {office.organizationName && office.organizationName !== office.nameRu && (
                <p className="org-name">{office.organizationName}</p>
              )}
              <p className="office-name">{office.nameRu}</p>
              <p className="office-contact">
                {[office.address, office.phone].filter(Boolean).join(' · ') || ' '}
              </p>
            </div>
          </div>

          <div className="wb-title">
            {t('Путевой лист')} № {waybill.number}
          </div>
          <div className="wb-subtitle">
            {dayjs(waybill.validFrom).format('DD.MM.YYYY HH:mm')} —{' '}
            {dayjs(waybill.validTo).format('DD.MM.YYYY HH:mm')}
          </div>

          <div className="wb-grid">
            <div className="cell">
              <span className="label">{t('Водитель')}</span>
              {driverName}
            </div>
            <div className="cell">
              <span className="label">{t('Табельный номер')}</span>
              {waybill.driver?.personnelNumber ?? '—'}
            </div>
            {waybill.coDriver && (
              <>
                <div className="cell">
                  <span className="label">{t('Второй водитель')}</span>
                  {personName(waybill.coDriver)}
                </div>
                <div className="cell">
                  <span className="label">{t('Табельный номер')}</span>
                  {waybill.coDriver.personnelNumber}
                </div>
              </>
            )}
            <div className="cell">
              <span className="label">{t('Техника')}</span>
              {waybill.vehicle?.garageNumber ?? '—'}{' '}
              {waybill.vehicle?.plateNumber ? `(${waybill.vehicle.plateNumber})` : ''}
            </div>
            {/* У машины один счётчик — печатаем только его. */}
            {meterType === 'ENGINE_HOURS' ? (
              <>
                <div className="cell">
                  <span className="label">{t('Наработка за смену')}</span>
                  {fmt(waybill.engineHours, 1)} мч
                </div>
                <div className="cell">
                  <span className="label">{t('Моточасы')}</span>
                  {fmt(waybill.engineHoursStart)} → {fmt(waybill.engineHoursEnd)}
                </div>
              </>
            ) : meterType === 'ODOMETER' ? (
              <>
                <div className="cell">
                  <span className="label">{t('Пробег за смену')}</span>
                  {fmt(waybill.distanceKm, 1)} км
                </div>
                <div className="cell">
                  <span className="label">{t('Одометр')}</span>
                  {fmt(waybill.odometerStart)} → {fmt(waybill.odometerEnd)}
                </div>
              </>
            ) : null}
            <div className="cell">
              <span className="label">{t('Топливо на начало')}</span>
              {fmt(waybill.fuelOpening, 2)} л
            </div>
            <div className="cell">
              <span className="label">{t('Заправка')}</span>
              {fmt(waybill.fuelIssued, 2)} л
            </div>
            <div className="cell">
              <span className="label">{t('Норма, л')}</span>
              {fmt(waybill.fuelNorm, 2)}
            </div>
            <div className="cell">
              <span className="label">{t('Факт, л')}</span>
              {fmt(waybill.fuelConsumed, 2)}
            </div>
          </div>

          {waybill.tasks.length > 0 && (
            <>
              <Typography.Title level={5}>{t('Задания')}</Typography.Title>
              <table className="wb-table">
                <thead>
                  <tr>
                    <th>№</th>
                    {isRegionDistrictLayout ? (
                      <>
                        <th>{t('Регион')}</th>
                        <th>{t('Район')}</th>
                        <th>{t('Адрес Б')}</th>
                      </>
                    ) : isAddressLayout ? (
                      <>
                        <th>{t('Адрес А')}</th>
                        {hasLocationList && <th>{t('Локация')}</th>}
                        <th>{t('Адрес Б')}</th>
                      </>
                    ) : (
                      <>
                        <th>{t('Рейс')}</th>
                        <th>{t('Борт')}</th>
                        <th>{t('Стоянка')}</th>
                        <th>{t('Куда')}</th>
                      </>
                    )}
                    <th>{t('км')}</th>
                    <th>{t('мч')}</th>
                  </tr>
                </thead>
                <tbody>
                  {waybill.tasks.map((task) => (
                    <Fragment key={task.id}>
                    <tr>
                      <td>{task.sequence}</td>
                      {isRegionDistrictLayout ? (
                        <>
                          <td>{task.fromPoint ?? '—'}</td>
                          <td>{task.aircraftReg ?? '—'}</td>
                          <td>{task.toPoint ?? '—'}</td>
                        </>
                      ) : isAddressLayout ? (
                        <>
                          <td>{task.fromPoint ?? '—'}</td>
                          {hasLocationList && <td>{task.aircraftReg ?? '—'}</td>}
                          <td>{task.toPoint ?? '—'}</td>
                        </>
                      ) : (
                        <>
                          <td>{task.flightNumber ?? '—'}</td>
                          <td>{task.aircraftReg ?? '—'}</td>
                          <td>{task.standNumber ?? '—'}</td>
                          <td>{task.toPoint ?? '—'}</td>
                        </>
                      )}
                      <td>{fmt(task.distanceKm, 1)}</td>
                      <td>{fmt(task.engineHours, 1)}</td>
                    </tr>
                    {/* Примечание задания — для заданий по заявке это номер
                        заявки, время подачи, заказчик с телефоном и цель:
                        водитель должен знать, когда ехать и куда звонить. */}
                    {task.notes && (
                      <tr className="wb-task-note">
                        <td />
                        <td colSpan={taskColumns - 1}>{task.notes}</td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {waybill.notes && (
            <Typography.Paragraph>
              <strong>{t('Примечание')}:</strong> {waybill.notes}
            </Typography.Paragraph>
          )}

          {/*
        Заключения трёх сторон, допустивших рейс. ФИО и время — из записей,
        по которым лист был выдан; подпись ставится от руки. Если заключения
        нет, потому что лист выдан до учёта осмотров, строка пустая — её заполняют
        вручную. Выдача в обход допуска печатается явно, с причиной.
      */}
          <Typography.Title level={5}>{t('Заключения')}</Typography.Title>
          <table className="wb-table wb-conclusions">
            <thead>
              <tr>
                <th style={{ width: '17%' }}>{t('Кто')}</th>
                <th style={{ width: '25%' }}>{t('Заключение')}</th>
                <th>{t('ФИО')}</th>
                <th style={{ width: '17%' }}>{t('Дата и время')}</th>
                <th style={{ width: '17%' }}>{t('Подпись')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('Медработник')}</td>
                {conclusions.medical || !conclusions.medicalBypass ? (
                  <>
                    <td>
                      {conclusions.medical
                        ? t(
                            MEDICAL_RESULT[conclusions.medical.result] ??
                              conclusions.medical.result,
                          )
                        : ''}
                    </td>
                    <td>{conclusions.medical?.name ?? ''}</td>
                    <td>{stamp(conclusions.medical?.checkedAt)}</td>
                    <td />
                  </>
                ) : (
                  // Выдан в обход: строка не пустая — иначе туда можно было бы
                  // вписать допуск задним числом. Подпись ставит диспетчер.
                  <td colSpan={4} className="wb-bypass">
                    {t('Выдан без заключения по решению диспетчера')}
                    {conclusions.medicalBypass.reason
                      ? `. ${t('Причина')}: ${conclusions.medicalBypass.reason}`
                      : ''}
                  </td>
                )}
              </tr>
              <tr>
                <td>{t('Механик')}</td>
                {conclusions.technical || !conclusions.technicalBypass ? (
                  <>
                    <td>
                      {conclusions.technical
                        ? t(
                            TECHNICAL_RESULT[conclusions.technical.result] ??
                              conclusions.technical.result,
                          )
                        : ''}
                    </td>
                    <td>{conclusions.technical?.name ?? ''}</td>
                    <td>{stamp(conclusions.technical?.checkedAt)}</td>
                    <td />
                  </>
                ) : (
                  // Выдан в обход: строка не пустая — иначе туда можно было бы
                  // вписать допуск задним числом. Подпись ставит диспетчер.
                  <td colSpan={4} className="wb-bypass">
                    {t('Выдан без заключения по решению диспетчера')}
                    {conclusions.technicalBypass.reason
                      ? `. ${t('Причина')}: ${conclusions.technicalBypass.reason}`
                      : ''}
                  </td>
                )}
              </tr>
              <tr>
                <td>{t('Диспетчер')}</td>
                <td>{conclusions.dispatcher ? t('Путевой лист выдан') : ''}</td>
                <td>{conclusions.dispatcher?.name ?? ''}</td>
                <td>{stamp(conclusions.dispatcher?.issuedAt)}</td>
                <td />
              </tr>
            </tbody>
          </table>

          <div className="wb-signatures">
            <div className="wb-signature-line">
              {t('Водитель')} ({driverName})
            </div>
          </div>

          <div className="wb-footer">
            {t('Сформировано')}: {dayjs(printedAt).format('DD.MM.YYYY HH:mm')}
          </div>
        </FitToPage>
      </div>
    </div>
  );
}

/**
 * Подгоняет содержимое под один лист: если оно выше листа, уменьшает масштаб
 * (CSS zoom) ровно настолько, чтобы поместилось. Пересчитывается при любом
 * изменении размера — догрузился логотип, сменился язык.
 *
 * Масштаб подбирается в несколько проходов: при уменьшении строки становятся
 * шире, текст переносится реже, и высота меняется не строго пропорционально.
 */
function FitToPage({
  children,
  onFit,
}: {
  children: ReactNode;
  /** Итог подгонки — документ показывает по нему предупреждение над листом. */
  onFit: (state: FitState) => void;
}) {
  const sheet = useRef<HTMLDivElement>(null);
  const report = useRef(onFit);
  report.current = onFit;
  const last = useRef<FitState>({ scale: 1, overflow: false });

  useLayoutEffect(() => {
    const element = sheet.current;
    const page = element?.parentElement;
    if (!element || !page) return;

    const fit = (): void => {
      const style = getComputedStyle(page);
      const available =
        page.clientHeight -
        parseFloat(style.paddingTop) -
        parseFloat(style.paddingBottom);
      let scale = 1;
      element.style.zoom = '1';
      let height = element.getBoundingClientRect().height;
      for (let pass = 0; pass < 4 && height > available + 0.5; pass++) {
        scale = Math.max(MIN_SCALE, scale * (available / height) * 0.995);
        element.style.zoom = String(scale);
        height = element.getBoundingClientRect().height;
      }
      const overflow = height > available + 0.5;
      if (Math.abs(last.current.scale - scale) >= 0.01 || last.current.overflow !== overflow) {
        last.current = { scale, overflow };
        report.current(last.current);
      }
    };

    fit();
    const observer = new ResizeObserver(() => {
      // Изменения, вызванные самой подгонкой, тоже приходят сюда; пересчёт
      // даёт тот же масштаб, поэтому зацикливания нет.
      requestAnimationFrame(fit);
    });
    observer.observe(element);
    for (const image of element.querySelectorAll('img'))
      image.addEventListener('load', fit);
    window.addEventListener('beforeprint', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('beforeprint', fit);
    };
  }, []);

  return <div ref={sheet}>{children}</div>;
}

interface FitState {
  /** Масштаб содержимого листа, 1 — без уменьшения. */
  scale: number;
  /** Не поместилось даже в минимальном масштабе — низ листа обрезан. */
  overflow: boolean;
}

/** Меньше этого масштаба лист не ужимается — дальше текст не прочесть. */
const MIN_SCALE = 0.25;
/** Ниже этого масштаба шрифт на бумаге уже трудно читать — предупреждаем. */
const READABLE_SCALE = 0.6;

/** Заключение медработника по результату предрейсового осмотра. */
const MEDICAL_RESULT: Record<string, string> = {
  PASSED: 'Допущен к рейсу',
  CONDITIONAL: 'Допущен с замечаниями',
  FAILED: 'Не допущен',
};

/** Заключение механика по результату предрейсового техконтроля. */
const TECHNICAL_RESULT: Record<string, string> = {
  PASSED: 'Выпуск разрешён',
  CONDITIONAL: 'Выпуск разрешён с замечаниями',
  FAILED: 'Выпуск запрещён',
};

function stamp(value: string | null | undefined): string {
  return value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '';
}

/**
 * Логотип на бланке: офиса, а если у офиса своего нет — организации (так
 * отдаёт сервер). Нет ни того, ни другого — место не занимается, текст
 * шапки просто начинается от левого края.
 */
function PrintLogo({ officeId }: { officeId: number }) {
  const src = useAuthedImage(`/offices/${officeId}/logo`);
  if (!src) return null;
  return <img className="wb-logo" src={src} alt="" />;
}
